import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, X, ChevronRight, Clock, MessageCircle, Check, Loader2 } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { scrollToOrder } from "./common";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallUsPopup = () => {
  const { product, callus } = useContent();
  const [open, setOpen] = useState(false);

  const cfg = callus || {};
  const enabled = cfg.enabled !== false;
  const autoOpen = cfg.autoOpen !== false;
  const autoDelayMs = (Number(cfg.autoDelaySeconds) > 0 ? Number(cfg.autoDelaySeconds) : 4) * 1000;
  const reappearMinutes = Number(cfg.reappearMinutes) > 0 ? Number(cfg.reappearMinutes) : 0;
  const whatsappBadge = cfg.whatsappBadge !== false;
  const phone = cfg.phone || "+919399993389";
  const phoneDisplay = cfg.phoneDisplay || "+91 93999 93389";
  const whatsapp = cfg.whatsapp || "919399993389";
  const waMessage = cfg.whatsappMessage || "Hi, I'm interested in the Adrusta Ratnam. Please share the details.";
  const heading = cfg.heading || "Have a Question Before You Order?";
  const subtext = cfg.subtext || "Our team is one call away. Speak to us & reserve your energised Ratnam today.";
  const offerEnabled = cfg.offerEnabled !== false;
  const offerText = cfg.offerText || "";
  const greetEnabled = cfg.greetEnabled !== false;
  const greetText = cfg.greetText || "We're online — chat now!";
  const greetDelayMs = (Number(cfg.greetDelaySeconds) > 0 ? Number(cfg.greetDelaySeconds) : 6) * 1000;
  const leadEnabled = cfg.leadEnabled !== false;
  const leadTitle = cfg.leadTitle || "Or leave your number & we'll call you back";
  const waLink = `https://wa.me/${whatsapp}?text=${encodeURIComponent(waMessage)}`;

  const [leadName, setLeadName] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadDone, setLeadDone] = useState(false);

  const submitLead = async (e) => {
    e.preventDefault();
    if (leadPhone.replace(/\D/g, "").length < 7) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { name: leadName, phone: leadPhone, source: "popup" });
      setLeadDone(true);
      toast.success("Thank you! We'll call you back shortly.");
    } catch {
      toast.error("Could not submit — please try calling us directly.");
    } finally {
      setLeadSubmitting(false);
    }
  };

  const [showGreet, setShowGreet] = useState(false);
  useEffect(() => {
    if (!enabled || !greetEnabled) return;
    if (sessionStorage.getItem("chatGreetClosed") === "1") return;
    const t = setTimeout(() => setShowGreet(true), greetDelayMs);
    return () => clearTimeout(t);
  }, [enabled, greetEnabled, greetDelayMs]);
  const closeGreet = () => { setShowGreet(false); sessionStorage.setItem("chatGreetClosed", "1"); };

  useEffect(() => {
    if (!enabled || !autoOpen) return;
    // Decide whether the popup may auto-open based on the reappearance policy.
    let allowed = true;
    if (reappearMinutes === 0) {
      allowed = sessionStorage.getItem("callUsPopupClosed") !== "1";
    } else {
      const last = Number(localStorage.getItem("callUsPopupLastClose") || 0);
      allowed = !last || Date.now() - last >= reappearMinutes * 60000;
    }
    if (!allowed) return;
    const t = setTimeout(() => setOpen(true), autoDelayMs);
    return () => clearTimeout(t);
  }, [enabled, autoOpen, autoDelayMs, reappearMinutes]);

  if (!enabled) return null;

  const close = () => {
    setOpen(false);
    if (reappearMinutes === 0) sessionStorage.setItem("callUsPopupClosed", "1");
    else localStorage.setItem("callUsPopupLastClose", String(Date.now()));
  };

  const image = (product?.images && product.images[0]) || "";

  const handleOrder = () => {
    close();
    setTimeout(() => scrollToOrder(), 150);
  };

  return (
    <>
      {/* Floating chips — always available */}
      <div className="fixed z-[70] bottom-24 left-4 md:bottom-6 md:left-6 flex items-center gap-2">
        {greetEnabled && showGreet && !open && (
          <div
            data-testid="chat-greet-bubble"
            className="absolute bottom-full left-0 mb-3 w-max max-w-[240px] animate-[popIn_.3s_ease]"
          >
            <div className="relative flex items-start gap-2 rounded-2xl rounded-bl-sm bg-white px-3.5 py-2.5 pr-7 shadow-2xl border border-[#25D366]/30">
              <a
                data-testid="chat-greet-link"
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-[#0b3d24] leading-snug"
              >
                {greetText}
              </a>
              <button
                data-testid="chat-greet-close"
                onClick={closeGreet}
                className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:text-slate-700"
                aria-label="Dismiss greeting"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <span className="absolute -bottom-1.5 left-4 h-3 w-3 rotate-45 bg-white border-b border-r border-[#25D366]/30" />
            </div>
          </div>
        )}
        <button
          data-testid="call-us-float-btn"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full pl-3 pr-4 py-3 text-[#3a1002] font-semibold shadow-2xl transition-transform hover:scale-105 active:scale-95"
          style={{ background: "linear-gradient(180deg,#F2D06B 0%,#D4A62C 55%,#B8860B 100%)", border: "2px solid #8a6410" }}
          aria-label="Call us"
        >
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-[#5A0B1D]">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E9C767] opacity-50" />
            <Phone className="w-4 h-4 text-[#E9C767]" />
          </span>
          <span className="text-sm hidden sm:inline">Call Us</span>
        </button>
        <a
          data-testid="whatsapp-float-btn"
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          className="relative flex h-[52px] w-[52px] items-center justify-center rounded-full text-white shadow-2xl transition-transform hover:scale-105 active:scale-95"
          style={{ background: "#25D366", border: "2px solid #1da851" }}
          aria-label="Chat on WhatsApp"
        >
          <MessageCircle className="w-6 h-6" />
          {whatsappBadge && (
            <span data-testid="whatsapp-badge" className="absolute -top-1.5 -right-1.5 flex h-5 w-5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold text-white border border-white">1</span>
            </span>
          )}
        </a>
      </div>

      {open && (
        <div
          data-testid="call-us-popup"
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-[fadeIn_.25s_ease]"
            onClick={close}
          />
          <div
            className="relative w-full max-w-sm overflow-hidden rounded-3xl shadow-2xl animate-[popIn_.35s_cubic-bezier(.2,.9,.3,1.2)]"
            style={{ background: "linear-gradient(180deg,#640E22 0%,#4C0817 100%)", border: "1px solid rgba(233,199,103,.35)" }}
          >
            <button
              data-testid="call-us-popup-close"
              onClick={close}
              className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/30 text-[#F4E3B8] hover:bg-black/50 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative pt-7 px-6 text-center">
              <span className="inline-block font-marcellus tracking-[0.25em] text-[10px] uppercase text-[#E9C767] mb-3">
                Talk To Our Astrologer
              </span>
              <div className="mx-auto mb-4 h-40 w-40 overflow-hidden rounded-2xl border-2 border-[#C9992B]/60 bg-[#3a0712] shadow-lg">
                {image ? (
                  <img src={image} alt={product?.name || "Product"} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[#C9992B]/60 text-xs">Adrusta Ratnam</div>
                )}
              </div>
              <h3 className="font-display text-xl font-bold text-[#F4E3B8] leading-snug">{heading}</h3>
              <p className="mt-1.5 text-sm text-[#e0c9ac]">{subtext}</p>
              {offerEnabled && offerText && (
                <div
                  data-testid="call-us-popup-offer"
                  className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-[#E9C767]/50 bg-[#E9C767]/10 px-3 py-2.5"
                >
                  <Clock className="w-4 h-4 text-[#E9C767] shrink-0" />
                  <span className="text-xs sm:text-sm font-semibold text-[#F4E3B8]">{offerText}</span>
                </div>
              )}
            </div>

            <div className="p-6 pt-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <a
                  data-testid="call-us-popup-call-btn"
                  href={`tel:${phone}`}
                  className="flex items-center justify-center gap-2 rounded-xl bg-[#5A0B1D] py-3.5 font-semibold text-[#F4E3B8] border border-[#C9992B]/40 transition-transform hover:scale-[1.02] active:scale-100"
                >
                  <Phone className="w-4 h-4 text-[#E9C767]" />
                  Call
                </a>
                <a
                  data-testid="call-us-popup-whatsapp-btn"
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-xl py-3.5 font-semibold text-white transition-transform hover:scale-[1.02] active:scale-100"
                  style={{ background: "#25D366", border: "1px solid #1da851" }}
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
              <p className="text-center text-[11px] text-[#c9b59a]">Call us at {phoneDisplay}</p>
              <button
                data-testid="call-us-popup-order-btn"
                onClick={handleOrder}
                className="cta-pulse group flex w-full items-center justify-center gap-1 rounded-xl py-3.5 font-extrabold uppercase tracking-wide text-[#3a1002] transition-transform hover:scale-[1.02] active:scale-100"
                style={{ background: "linear-gradient(180deg,#F2D06B 0%,#D4A62C 55%,#B8860B 100%)", border: "2px solid #8a6410" }}
              >
                Order Now
                <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>

              {leadEnabled && (
                <div className="pt-1">
                  <div className="my-2 flex items-center gap-2">
                    <div className="h-px flex-1 bg-[#C9992B]/25" />
                    <span className="text-[10px] uppercase tracking-widest text-[#c9b59a]">or</span>
                    <div className="h-px flex-1 bg-[#C9992B]/25" />
                  </div>
                  {leadDone ? (
                    <div data-testid="lead-success" className="flex items-center justify-center gap-2 rounded-xl border border-[#25D366]/40 bg-[#25D366]/10 py-3 text-sm font-semibold text-[#7be0a0]">
                      <Check className="w-4 h-4" /> Thanks! We'll call you back shortly.
                    </div>
                  ) : (
                    <form data-testid="lead-form" onSubmit={submitLead} className="space-y-2">
                      <p className="text-center text-xs text-[#e0c9ac]">{leadTitle}</p>
                      <input
                        data-testid="lead-name-input"
                        type="text"
                        value={leadName}
                        onChange={(e) => setLeadName(e.target.value)}
                        placeholder="Your name (optional)"
                        className="w-full rounded-lg bg-[#3a0712] border border-[#C9992B]/30 px-3 py-2.5 text-sm text-[#F4E3B8] placeholder:text-[#a98f70] focus:outline-none focus:ring-1 focus:ring-[#C9992B]"
                      />
                      <input
                        data-testid="lead-phone-input"
                        type="tel"
                        value={leadPhone}
                        onChange={(e) => setLeadPhone(e.target.value)}
                        placeholder="Your phone number"
                        required
                        className="w-full rounded-lg bg-[#3a0712] border border-[#C9992B]/30 px-3 py-2.5 text-sm text-[#F4E3B8] placeholder:text-[#a98f70] focus:outline-none focus:ring-1 focus:ring-[#C9992B]"
                      />
                      <button
                        data-testid="lead-submit-btn"
                        type="submit"
                        disabled={leadSubmitting}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#5A0B1D] py-2.5 text-sm font-semibold text-[#F4E3B8] border border-[#C9992B]/50 transition-transform hover:scale-[1.01] active:scale-100 disabled:opacity-60"
                      >
                        {leadSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4 text-[#E9C767]" />}
                        Request a Callback
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CallUsPopup;
